/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
    configAuth,
    addPolygon,
    addPolyline,
    addSimpleMarker,
    createMapView,
    createWebMap,
    MapItem,
} from "../arcgisUtils";
import { isPlatform } from "@ionic/react";
import { ArcGisMapConfig } from "../models/ArcGisMapConfig";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapView from "@arcgis/core/views/MapView";

type Props = {
    mapConfig: ArcGisMapConfig;
    webMapItemId: string | null | undefined;
    center?: Point;
    items: MapItem[];
    showLegend?: boolean;
    token?: string;
};

const Map = ({ mapConfig, webMapItemId, center, items, showLegend, token }: Props) => {
    const mapEl = useRef(null);

    const [loggedIn, setLoggedIn] = useState(false);
    const [graphicsLayer, setGraphicsLayer] = useState<GraphicsLayer | null>(null);
    const [view, setView] = useState<MapView | null>(null);

    const getAppUrl = () =>
        isPlatform("android") ? "com.vislock.permits://localhost/" : `${process.env.REACT_APP_WEB_SERVER}/`;

    useEffect(() => {
        const initAuth = async () => {
            try {
                // Configure authentication but don't wait for it to complete
                // This registers the OAuth info so IdentityManager can handle auth when needed
                configAuth(mapConfig.authAppId, mapConfig.portalUrl, getAppUrl(), token);
                setLoggedIn(true); // Allow map to load, auth will happen on-demand
            } catch {
                console.error("Auth configuration failed");
                setLoggedIn(true); // Still allow map to load with public layers
            }
        };

        initAuth();
    }, [token, mapEl, mapConfig.authAppId, mapConfig.portalUrl]);

    useEffect(() => {
        const initMap = async () => {
            if (!webMapItemId || !loggedIn || !mapEl.current) {
                return;
            }

            console.log("Initializing map with token:", !!token);

            const { webmap, graphicsLayer } = createWebMap(webMapItemId);
            setGraphicsLayer(graphicsLayer);

            var gotoTargets = () => {
                // Filter to get Permit area for the Goto target.
                let targets = graphicsLayer.graphics.filter((g: any) => g.geometry.type === "polygon");

                // If there are no Permit area targets, fall back to the Project boundary target, which is a polyline.
                if (targets.length === 0) {
                    targets = graphicsLayer.graphics.filter((g: any) => g.geometry.type === "polyline");
                }

                return targets;
            };

            var view = createMapView(mapEl.current, webmap, showLegend ?? false, gotoTargets, center);
            setView(view);

            // Log when web map layers are loaded
            webmap.when(() => {
                console.log("Web map loaded with", webmap.layers.length, "layers");
                webmap.layers.forEach((layer: any) => {
                    console.log("Layer:", layer.title || layer.id, "visible:", layer.visible, "loaded:", layer.loaded);

                    layer
                        .when(() => {
                            console.log("Layer loaded successfully:", layer.title || layer.id);
                        })
                        .catch((error: any) => {
                            console.log("Layer failed to load:", layer.title || layer.id, error);
                        });
                });
            });
        };

        initMap();

        return () => {
            // clean up the map view
            if (view) {
                console.log("Cleaning up map view");
                view.destroy();
                setView(null);
            }
            if (graphicsLayer) {
                graphicsLayer.destroy();
                setGraphicsLayer(null);
            }
        };
    }, [mapEl, loggedIn, token, center]);

    useEffect(() => {
        if (!(graphicsLayer && view && items)) {
            return;
        }

        console.log("Updating graphics layer with", items.length, "items");
        graphicsLayer.removeAll();

        // Add items to the graphics layer
        items?.forEach((item) => {
            // Skip null or undefined items, or items without geometry
            if (!item || !item.geometry) {
                return;
            }

            var geometry = item.geometry;

            if (geometry.type === "point") {
                addSimpleMarker(view, new Point(geometry), item.color);
            } else if (geometry.type === "polygon") {
                addPolygon(graphicsLayer, new Polygon(geometry), item.color, item.style, item.popup);
            } else if (geometry.type === "polyline") {
                addPolyline(graphicsLayer, new Polyline(geometry), item.color, item.popup);
            }
        });
    }, [items, graphicsLayer, view]);

    return (
        <>
            <div
                className="webmap"
                ref={mapEl}
                style={{
                    height: "100%",
                    width: "100%",
                    position: "relative",
                    zIndex: 1,
                }}
            ></div>
        </>
    );
};

export default Map;
